/**
 * AnonymizerTorBlockPage — operator dashboard for the Tor / anonymizer
 * block rule set. Exposes: umbrella toggle, IP rule editor, recent
 * block rule table, and an inline evaluate probe. GREEN accent only.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAnonymizerTorBlockList,
	useAnonymizerTorBlockCreate,
	useAnonymizerTorBlockDelete,
	useAnonymizerTorBlockEvaluate,
} from "./hooks";
import type {
	AnonymizerTorBlockKind,
	AnonymizerTorBlockRule,
} from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const CARD_MUTED =
	"rounded-[8px] border border-zinc-200 bg-zinc-50 p-5";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-600 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

const UMBRELLA_VALUE = "anonymizer_umbrella";

function formatTime(seconds: number): string {
	if (!Number.isFinite(seconds) || seconds <= 0) return "-";
	const ms = Math.floor(seconds) * 1000;
	try {
		return new Date(ms).toISOString().replace("T", " ").slice(0, 19) + "Z";
	} catch {
		return "-";
	}
}

export function AnonymizerTorBlockPage() {
	const listQuery = useAnonymizerTorBlockList();
	const createMut = useAnonymizerTorBlockCreate();
	const deleteMut = useAnonymizerTorBlockDelete();
	const evaluateMut = useAnonymizerTorBlockEvaluate();

	const rows: AnonymizerTorBlockRule[] = useMemo(
		() => listQuery.data?.items ?? [],
		[listQuery.data],
	);

	const umbrellaRule = rows.find(
		(r) => r.kind === "anonymizer_umbrella",
	);
	const umbrellaOn = Boolean(umbrellaRule);

	const torIpRules = rows.filter((r) => r.kind === "tor_exit_ip");

	const [filter, setFilter] = useState<"all" | AnonymizerTorBlockKind>(
		"all",
	);
	const visibleRows = useMemo(() => {
		if (filter === "all") return rows;
		return rows.filter((r) => r.kind === filter);
	}, [rows, filter]);

	const [ipInput, setIpInput] = useState("");
	const [noteInput, setNoteInput] = useState("");

	const onAddTorIp = (e: FormEvent) => {
		e.preventDefault();
		const value = ipInput.trim().toLowerCase();
		if (!value) return;
		createMut.mutate(
			{
				kind: "tor_exit_ip",
				value,
				note: noteInput.trim() || null,
			},
			{
				onSuccess: () => {
					setIpInput("");
					setNoteInput("");
				},
			},
		);
	};

	const onToggleUmbrella = () => {
		if (umbrellaOn && umbrellaRule) {
			deleteMut.mutate(umbrellaRule.id);
			return;
		}
		createMut.mutate({
			kind: "anonymizer_umbrella",
			value: UMBRELLA_VALUE,
			note: null,
		});
	};

	const [probeIp, setProbeIp] = useState("");
	const [probeTor, setProbeTor] = useState(false);
	const [probeAnon, setProbeAnon] = useState(false);
	const onProbe = (e: FormEvent) => {
		e.preventDefault();
		evaluateMut.mutate({
			ip: probeIp.trim() || undefined,
			flags: { tor: probeTor, anonymizer: probeAnon },
		});
	};

	return (
		<main
			data-testid="anonymizer-tor-block-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					anonymizer / tor block
				</p>
				<h1 className="text-3xl font-semibold text-green-700">
					Anonymizer & Tor Block
				</h1>
				<p className="max-w-3xl text-sm text-zinc-600">
					Curate the tenant rule set that blocks Tor exit traffic and
					anonymizer networks. The umbrella toggle short-circuits any
					flagged request; IP rules drop exact Tor exit-node matches.
				</p>
			</header>

			<section
				className={CARD}
				aria-label="Anonymizer umbrella toggle"
				data-testid="anonymizer-tor-block-umbrella-card"
			>
				<div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
					<div>
						<h2 className="text-base font-semibold text-zinc-900">
							Anonymizer umbrella
						</h2>
						<p className="text-sm text-zinc-600">
							Block every request flagged as Tor or anonymizer traffic,
							regardless of IP list state.
						</p>
					</div>
					<div className="flex items-center gap-3">
						<span
							className={
								"inline-flex items-center gap-2 rounded-full border px-2.5 py-0.5 text-xs font-medium " +
								(umbrellaOn
									? "border-green-700 bg-green-50 text-green-800"
									: "border-zinc-300 bg-zinc-50 text-zinc-600")
							}
							data-testid="anonymizer-tor-block-umbrella-badge"
						>
							<span
								className={
									"inline-block h-1.5 w-1.5 rounded-full " +
									(umbrellaOn ? "bg-green-600" : "bg-zinc-400")
								}
							/>
							{umbrellaOn ? "On" : "Off"}
						</span>
						<button
							type="button"
							role="switch"
							aria-checked={umbrellaOn}
							aria-label="Toggle anonymizer umbrella"
							onClick={onToggleUmbrella}
							disabled={createMut.isPending || deleteMut.isPending}
							className={
								"relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-green-600/30 disabled:cursor-not-allowed disabled:opacity-60 " +
								(umbrellaOn
									? "border-green-700 bg-green-600"
									: "border-zinc-300 bg-zinc-200")
							}
							data-testid="anonymizer-tor-block-umbrella-toggle"
						>
							<span
								className={
									"inline-block h-5 w-5 transform rounded-full bg-white shadow transition " +
									(umbrellaOn ? "translate-x-5" : "translate-x-0.5")
								}
							/>
						</button>
					</div>
				</div>
				{createMut.error && createMut.variables?.kind === "anonymizer_umbrella" ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="anonymizer-tor-block-umbrella-error"
					>
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Add Tor exit IP rule">
				<h2 className="text-base font-semibold text-zinc-900">
					Add Tor exit IP
				</h2>
				<p className="mt-1 text-sm text-zinc-600">
					IPv4 literals only. Duplicates collapse into the existing rule.
				</p>
				<form
					onSubmit={onAddTorIp}
					className="mt-3 flex flex-col gap-3 sm:flex-row"
					data-testid="anonymizer-tor-block-create-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="e.g. 185.220.101.42"
						value={ipInput}
						onChange={(e) => setIpInput(e.currentTarget.value)}
						disabled={createMut.isPending}
						aria-label="Tor exit IP"
						data-testid="anonymizer-tor-block-ip-input"
					/>
					<input
						type="text"
						className={INPUT}
						placeholder="Note (optional)"
						value={noteInput}
						onChange={(e) => setNoteInput(e.currentTarget.value)}
						disabled={createMut.isPending}
						aria-label="Rule note"
						data-testid="anonymizer-tor-block-note-input"
					/>
					<button
						type="submit"
						className={BTN}
						disabled={createMut.isPending || !ipInput.trim()}
						data-testid="anonymizer-tor-block-submit"
					>
						{createMut.isPending &&
						createMut.variables?.kind === "tor_exit_ip"
							? "Saving"
							: "Add rule"}
					</button>
				</form>
				{createMut.error && createMut.variables?.kind === "tor_exit_ip" ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="anonymizer-tor-block-create-error"
					>
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Evaluate probe">
				<h2 className="text-base font-semibold text-zinc-900">
					Evaluate probe
				</h2>
				<p className="mt-1 text-sm text-zinc-600">
					Dry-run a candidate signal through the tenant rule set.
				</p>
				<form
					onSubmit={onProbe}
					className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end"
					data-testid="anonymizer-tor-block-probe-form"
				>
					<label className="flex w-full flex-col gap-1 text-xs font-medium text-zinc-600">
						IP
						<input
							type="text"
							className={INPUT}
							placeholder="1.2.3.4"
							value={probeIp}
							onChange={(e) => setProbeIp(e.currentTarget.value)}
							data-testid="anonymizer-tor-block-probe-ip"
						/>
					</label>
					<label className="flex items-center gap-2 text-sm text-zinc-700">
						<input
							type="checkbox"
							className="h-4 w-4 rounded border-zinc-300 text-green-700 focus:ring-green-600/30"
							checked={probeTor}
							onChange={(e) => setProbeTor(e.currentTarget.checked)}
							data-testid="anonymizer-tor-block-probe-tor"
						/>
						tor flag
					</label>
					<label className="flex items-center gap-2 text-sm text-zinc-700">
						<input
							type="checkbox"
							className="h-4 w-4 rounded border-zinc-300 text-green-700 focus:ring-green-600/30"
							checked={probeAnon}
							onChange={(e) => setProbeAnon(e.currentTarget.checked)}
							data-testid="anonymizer-tor-block-probe-anon"
						/>
						anonymizer flag
					</label>
					<button
						type="submit"
						className={BTN}
						disabled={evaluateMut.isPending}
						data-testid="anonymizer-tor-block-probe-submit"
					>
						{evaluateMut.isPending ? "Evaluating" : "Evaluate"}
					</button>
				</form>
				{evaluateMut.data ? (
					<p
						className="mt-3 text-sm"
						data-testid="anonymizer-tor-block-probe-result"
					>
						<span
							className={
								"inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold " +
								(evaluateMut.data.blocked
									? "border-red-300 bg-red-50 text-red-700"
									: "border-green-300 bg-green-50 text-green-800")
							}
						>
							{evaluateMut.data.blocked ? "Blocked" : "Allowed"}
						</span>
						{evaluateMut.data.rule ? (
							<span className="ml-2 font-mono text-xs text-zinc-600">
								{evaluateMut.data.rule.kind}:{evaluateMut.data.rule.value}
							</span>
						) : null}
					</p>
				) : null}
			</section>

			<section
				className={CARD}
				aria-label="Block rules"
				data-testid="anonymizer-tor-block-rules-card"
			>
				<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Block rules
					</h2>
					<div
						className="flex flex-wrap gap-2"
						role="group"
						aria-label="Filter by kind"
						data-testid="anonymizer-tor-block-filter"
					>
						{(
							[
								{ id: "all", label: `All (${rows.length})` },
								{
									id: "tor_exit_ip",
									label: `Tor IPs (${torIpRules.length})`,
								},
								{
									id: "anonymizer_umbrella",
									label: `Umbrella (${umbrellaOn ? 1 : 0})`,
								},
							] as const
						).map((opt) => (
							<button
								key={opt.id}
								type="button"
								onClick={() =>
									setFilter(
										opt.id as "all" | AnonymizerTorBlockKind,
									)
								}
								className={
									"rounded-[6px] border px-2.5 py-1 text-xs font-medium transition-colors " +
									(filter === opt.id
										? "border-green-700 bg-green-700 text-white"
										: "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50")
								}
								aria-pressed={filter === opt.id}
								data-testid={`anonymizer-tor-block-filter-${opt.id}`}
							>
								{opt.label}
							</button>
						))}
					</div>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="anonymizer-tor-block-loading"
					>
						Loading rules.
					</p>
				) : listQuery.error ? (
					<p
						className="mt-4 text-sm text-red-600"
						role="alert"
						data-testid="anonymizer-tor-block-list-error"
					>
						{listQuery.error.message}
					</p>
				) : visibleRows.length === 0 ? (
					<div
						className={CARD_MUTED + " mt-4"}
						data-testid="anonymizer-tor-block-empty"
					>
						<p className="text-sm text-zinc-600">
							No rules match this filter yet.
						</p>
					</div>
				) : (
					<div className="mt-4 overflow-hidden rounded-[6px] border border-zinc-200">
						<table
							className="w-full border-collapse text-sm"
							data-testid="anonymizer-tor-block-list"
						>
							<thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
								<tr>
									<th className="px-3 py-2 text-left font-semibold">
										Kind
									</th>
									<th className="px-3 py-2 text-left font-semibold">
										Value
									</th>
									<th className="px-3 py-2 text-left font-semibold">
										Note
									</th>
									<th className="px-3 py-2 text-left font-semibold">
										Added
									</th>
									<th className="px-3 py-2 text-right font-semibold">
										Action
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-200 bg-white">
								{visibleRows.map((row) => (
									<tr
										key={row.id}
										data-testid={`anonymizer-tor-block-row-${row.id}`}
									>
										<td className="px-3 py-2">
											<span
												className={
													"inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold " +
													(row.kind === "anonymizer_umbrella"
														? "border-green-700 bg-green-50 text-green-800"
														: "border-zinc-300 bg-zinc-50 text-zinc-700")
												}
											>
												{row.kind}
											</span>
										</td>
										<td className="px-3 py-2 font-mono text-xs text-zinc-700">
											{row.value}
										</td>
										<td className="px-3 py-2 text-zinc-600">
											{row.note ?? "-"}
										</td>
										<td className="px-3 py-2 text-xs text-zinc-500">
											{formatTime(row.created_at)}
										</td>
										<td className="px-3 py-2 text-right">
											<button
												type="button"
												className={BTN_DANGER}
												onClick={() => deleteMut.mutate(row.id)}
												disabled={deleteMut.isPending}
												data-testid={`anonymizer-tor-block-delete-${row.id}`}
											>
												Remove
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
				{deleteMut.error ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="anonymizer-tor-block-delete-error"
					>
						{deleteMut.error.message}
					</p>
				) : null}
				<div className="mt-3 flex items-center justify-end">
					<button
						type="button"
						className={BTN_GHOST}
						onClick={() => listQuery.refetch()}
						disabled={listQuery.isFetching}
						data-testid="anonymizer-tor-block-refresh"
					>
						{listQuery.isFetching ? "Refreshing" : "Refresh"}
					</button>
				</div>
			</section>
		</main>
	);
}

export default AnonymizerTorBlockPage;
